import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CloudUpload, Download, RefreshCw, Wifi, WifiOff } from 'lucide-react';
import { OFFLINE_REASONS } from '@shripos/shared/offline';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  EmptyState,
  TBody,
  TD,
  TH,
  THead,
  TR,
  Table,
  cn,
  formatDateTime,
} from '@shripos/ui';
import { flushOutbox, outboxItems, retryFailed } from '../lib/offline/index.js';
import { rupees } from '../lib/money.js';
import { useOnline } from '../lib/realtime.js';
import { useOffline } from '../stores/offline.js';

function hoursLeft(ms) {
  if (ms == null) return null;
  const h = Math.floor(ms / 3_600_000);
  return h >= 1 ? `${h} h` : `${Math.max(1, Math.round(ms / 60_000))} min`;
}

function OutboxDialog({ open, onClose }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const { pending, failed, syncing, reachable, permit, lastSyncAt } = useOffline();
  useEffect(() => {
    if (open) outboxItems().then(setItems);
  }, [open, pending, failed]);
  const exportFile = () => {
    const blob = new Blob([JSON.stringify(items, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `unsynced-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
  };
  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t('sync.title', 'Waiting to sync')}
      description={
        reachable
          ? t('sync.connected', 'Connected. Bills, open orders and kitchen updates made offline upload automatically.')
          : t(
              'sync.offline',
              'No internet. Bills, open orders and kitchen updates are kept on this device and upload when the connection is back.',
            )
      }
      footer={
        <>
          {items.length > 0 && (
            <Button variant="secondary" icon={Download} onClick={exportFile}>
              {t('sync.saveCopy', 'Save a copy')}
            </Button>
          )}
          <Button icon={RefreshCw} loading={syncing} disabled={!reachable || !pending} onClick={() => flushOutbox()}>
            {t('sync.now', 'Sync now')}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        {permit && (
          <p className="text-sm text-slate-600">
            {permit.enabled
              ? t('sync.permitLeft', 'This device can bill offline for about {{time}} more.', {
                  time: hoursLeft(permit.remainingMs),
                })
              : t(`offline.reason.${permit.reason}`, {
                  defaultValue: OFFLINE_REASONS[permit.reason] ?? 'Offline billing is not available on this device.',
                })}
            {lastSyncAt && ` ${t('sync.lastSync', 'Last sync {{at}}.', { at: formatDateTime(lastSyncAt) })}`}
          </p>
        )}
        {failed > 0 && (
          <Alert tone="danger" title={t('sync.attention', '{{count}} item(s) need attention', { count: failed })}>
            {t(
              'sync.attentionBody',
              'The server refused them (see the reason). Fix the cause and press Retry, or save a copy for support. They are never deleted from this device automatically.',
            )}
          </Alert>
        )}
        {items.length === 0 ? (
          <EmptyState
            icon={CloudUpload}
            title={t('sync.nothing', 'Nothing waiting')}
            description={t('sync.nothingBody', 'Everything done on this device is on the server.')}
          />
        ) : (
          <div className="max-h-80 overflow-auto">
            <Table>
              <THead>
                <TR>
                  <TH>{t('sync.item', 'Bill / order')}</TH>
                  <TH>{t('sync.made', 'Made')}</TH>
                  <TH className="text-right">{t('so.total', 'Total')}</TH>
                  <TH>{t('sync.status', 'Status')}</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {items.map((i) => (
                  <TR key={i.clientUuid}>
                    <TD className="font-medium">{i.label ?? i.invoiceNo}</TD>
                    <TD>{formatDateTime(i.createdAt)}</TD>
                    <TD className="text-right tabular-nums">{i.totalPaise != null ? rupees(i.totalPaise) : '—'}</TD>
                    <TD>
                      {i.status === 'failed' ? (
                        <span className="text-sm text-red-700">{i.error?.message ?? t('sync.refused', 'Refused')}</span>
                      ) : (
                        <Badge tone="warning">{t('sync.waiting', 'Waiting')}</Badge>
                      )}
                    </TD>
                    <TD>
                      {i.status === 'failed' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={!reachable}
                          onClick={() => retryFailed(i.clientUuid).then(() => outboxItems().then(setItems))}
                        >
                          {t('sync.retry', 'Retry')}
                        </Button>
                      )}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </div>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Top-bar status: online / reconnecting / offline, plus bills waiting to sync (spec §4.10:
 * "clear online / offline / sync-pending indicator"). Tap for the list.
 */
export function SyncStatus({ socketConnected }) {
  const { t } = useTranslation();
  const browserOnline = useOnline();
  const { reachable, pending, failed, syncing } = useOffline();
  const [open, setOpen] = useState(false);
  const online = browserOnline && reachable;
  const state = !online ? 'offline' : socketConnected ? 'online' : 'reconnecting';
  const Icon = failed ? AlertTriangle : online ? (pending ? CloudUpload : Wifi) : WifiOff;
  const label = failed
    ? t('sync.toCheck', '{{count}} to check', { count: failed })
    : pending
      ? syncing
        ? t('sync.syncing', 'Syncing…')
        : t('sync.toSync', '{{count}} to sync', { count: pending })
      : t(`common.${state}`);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="sync-status"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
          failed ? 'bg-red-100 text-red-800' : state === 'online' && !pending && 'bg-emerald-50 text-emerald-700',
          !failed && (pending || state === 'reconnecting') && 'bg-amber-50 text-amber-800',
          !failed && state === 'offline' && !pending && 'bg-red-50 text-red-700',
        )}
        title={
          state === 'offline'
            ? t('sync.noInternet', 'No internet connection')
            : state === 'reconnecting'
              ? t('sync.liveReconnecting', 'Live updates reconnecting')
              : t('sync.connectedShort', 'Connected')
        }
      >
        <Icon className={cn('size-3.5', syncing && 'animate-pulse')} />
        <span className={cn(!pending && !failed && 'hidden sm:inline')}>{label}</span>
      </button>
      <OutboxDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
